import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import moment from 'moment';
import TrackingTypes from '../../../../../manifests/trackingTypes';
import { useStore } from '../../../../../stores/root';
import GlobalConfig from '../../../../../config';

// assets
import { faArrowRight, faBoxArchive, faCheck, faComment, faCopy, faContainerStorage, faPaperPlane, faRotate, faShip, faTruck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import OrderService from '../../../../../services/orders/order';
import OrderTrackingService from '../../../../../services/orders/trackings/orderTracking';

// props
import { IOrderTrackingResourceShortProps } from '../../../../../props/orders/trackings/orderTracking';

// components
import { ActionButton, CommandBar, DefaultButton, Dropdown, ICommandBarItemProps, IContextualMenuItem, IDropdownOption, Link, Panel, PanelType, PrimaryButton, Stack, TextField } from '@fluentui/react';
import Text from './../../../../typography/text';
import Tag, { TTagVariant } from '../../../../uiframeworks/tag';
import { IOrderResourceProps } from '../../../../../props/orders/order';
import Label from '../../../../typography/label';
import LoadingComponent from '../../../../feedbacks/loading';
import OrderTrackingForm from '../form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import DeleteOrderTrackingForm from '../delete';
import { IOrderContainerResourceShortProps } from '../../../../../props/orders/containers';
import OrderContainerDetails from '../../../containers/general/details';
import { NavLink } from 'react-router-dom';
import { IOrderTruckResourceShortProps } from '../../../../../props/orders/trucks';
import OrderTruckDetails from '../../../trucks/general/details';
import OrderTrackingStatusForm from '../status';
import TrainScheduleStatusForm from '../../../../operationals/trains/schedules/general/status';
import ShipScheduleStatusForm from '../../../../operationals/ships/schedules/general/status';
import UploadFilesPanel from '../../../../uiframeworks/files/uploads/panel';
import { IFileDetailsProps } from '../../../../../props/general';
import SortOrderTrackingForm from '../form/sort';
import OrderTrackingDetailsCard from '../details/card';
import PermissionsService from '../../../../../services/permissions';
import { templateTrackings } from '../../../../../manifests/templateTrackings';

interface IOrderTrackingsListProps {
    qs?: string[];
    orderId: string;
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateTracking?: boolean;
    variant?: 'card' | 'plain';
    containerId?: string;
    truckId?: string;
}

const OrderTrackingsList: React.FC<IOrderTrackingsListProps> = (props: IOrderTrackingsListProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [order, setOrder] = React.useState<IOrderResourceProps | undefined>();
    const [trackings, setTrackings] = React.useState<IOrderTrackingResourceShortProps[]>([]);
    const [activeTracking, setActiveTracking] = React.useState<IOrderTrackingResourceShortProps | undefined>();
    const [activeContainer, setActiveContainer] = React.useState<IOrderContainerResourceShortProps | undefined>();
    const [activeOrderTruck, setActiveOrderTruck] = React.useState<IOrderTruckResourceShortProps | undefined>();
    const [selectedFilter, setSelectedFilter] = React.useState<string>(props.containerId || props.truckId || 'all');
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const [trackingMessageText, setTrackingMessageText] = React.useState<string>('');
    const [detectedCategory, setDetectedCategory] = React.useState<string>('');
    const [randomTrigger, setRandomTrigger] = React.useState<number>(0);

    useEffect(() => {
        if (activeSurface === 'share.message' && order) {
            // Find the latest active (in progress, sailing, or completed) tracking, filtering out future estimated/scheduled stages
            const activeOrCompleted = trackings.filter(t => {
                const { dooringDetails, loadDetails, unloadingDetails, shippingDetails, trainDetails } = t;
                if (dooringDetails) return dooringDetails.status === 'in progress' || dooringDetails.status === 'completed';
                if (loadDetails || unloadingDetails) return t.status === 'in progress' || t.status === 'completed';
                if (shippingDetails) return shippingDetails.schedule.status === 'sailing' || shippingDetails.schedule.status === 'arrived';
                if (trainDetails) return trainDetails.schedule.status === 'in progress' || trainDetails.schedule.status === 'arrived';
                return t.status === 'in progress' || t.status === 'completed';
            });
            const targetTracking = activeTracking || activeOrCompleted[0] || trackings[0];

            if (!targetTracking) {
                setTrackingMessageText('');
                setDetectedCategory('No tracking status available');
                return;
            }
            const type = targetTracking.type;
            const trackingTypeObj = TrackingTypes.find((t) => t.key === type);
            const trackingTypeName = trackingTypeObj ? trackingTypeObj.text : 'Tracking';

            let statusKey: 'estimated' | 'in_progress' | 'completed' = 'estimated';
            let statusLabel = 'Estimasi (Estimated)';

            const { dooringDetails, loadDetails, unloadingDetails, shippingDetails, trainDetails } = targetTracking;

            if (dooringDetails) {
                statusLabel = 'Estimasi Pengiriman (Estimated)';
                if (dooringDetails.status === 'in progress') {
                    statusKey = 'in_progress';
                    statusLabel = 'Dalam Perjalanan (In Transit)';
                } else if (dooringDetails.status === 'completed') {
                    statusKey = 'completed';
                    statusLabel = 'Tiba di Tujuan (Arrived)';
                }
            } else if (loadDetails) {
                statusLabel = 'Estimasi Mulai Muat (Estimated Start)';
                if (targetTracking.status === 'in progress') {
                    statusKey = 'in_progress';
                    statusLabel = 'Mulai Muat (Loading)';
                } else if (targetTracking.status === 'completed') {
                    statusKey = 'completed';
                    statusLabel = 'Selesai Muat (Loaded)';
                }
            } else if (unloadingDetails) {
                statusLabel = 'Estimasi Mulai Bongkar (Estimated Start)';
                if (targetTracking.status === 'in progress') {
                    statusKey = 'in_progress';
                    statusLabel = 'Mulai Bongkar (Unloading)';
                } else if (targetTracking.status === 'completed') {
                    statusKey = 'completed';
                    statusLabel = 'Selesai Bongkar (Unloaded)';
                }
            } else if (shippingDetails) {
                statusLabel = 'Scheduled';
                if (shippingDetails.schedule.status === 'sailing') {
                    statusKey = 'in_progress';
                    statusLabel = 'Sailing';
                } else if (shippingDetails.schedule.status === 'arrived') {
                    statusKey = 'completed';
                    statusLabel = 'Arrived';
                } else if (shippingDetails.schedule.status === 'cancelled') {
                    statusLabel = 'Cancelled';
                }
            } else if (trainDetails) {
                statusLabel = 'Estimasi Keberangkatan Kereta (Estimated Departure)';
                if (trainDetails.schedule.status === 'in progress') {
                    statusKey = 'in_progress';
                    statusLabel = 'Dalam Perjalanan (In Transit)';
                } else if (trainDetails.schedule.status === 'arrived') {
                    statusKey = 'completed';
                    statusLabel = 'Tiba di Depo/Stasiun (Arrived)';
                }
            }

            setDetectedCategory(`${trackingTypeName} - ${statusLabel}`);

            const custName = order.customer?.name;
            const compName = order.company?.name;
            const clientName = custName && compName ? `${custName} - ${compName}` : (custName || compName || 'Bapak/Ibu');
            const orderNo = order.orderNumber || '';

            let additionalFields = '';
            if (shippingDetails) {
                additionalFields += `\n• Nama Kapal: ${shippingDetails.ship.name || '-'}`;
                additionalFields += `\n• Voyage: ${shippingDetails.schedule.voy || '-'}`;
                if (targetTracking.container) {
                    additionalFields += `\n• No. Container: ${targetTracking.container.containerNumber || '-'}`;
                }
                const routes: string[] = [];
                if (shippingDetails.schedule.originAddress) routes.push(shippingDetails.schedule.originAddress);
                if (shippingDetails.schedule.destinationAddress) routes.push(shippingDetails.schedule.destinationAddress);
                if (routes.length > 0) {
                    additionalFields += `\n• Rute: ${routes.join(' -> ')}`;
                }
            } else if (trainDetails) {
                additionalFields += `\n• Nama Kereta: ${trainDetails.train.name || '-'}`;
                additionalFields += `\n• Voyage: ${trainDetails.schedule.voy || '-'}`;
                if (targetTracking.container) {
                    additionalFields += `\n• No. Container: ${targetTracking.container.containerNumber || '-'}`;
                }
                const routes: string[] = [];
                if (trainDetails.schedule.originAddress) routes.push(trainDetails.schedule.originAddress);
                if (trainDetails.schedule.destinationAddress) routes.push(trainDetails.schedule.destinationAddress);
                if (routes.length > 0) {
                    additionalFields += `\n• Rute: ${routes.join(' -> ')}`;
                }
            } else if (dooringDetails) {
                if (dooringDetails.truck) {
                    additionalFields += `\n• Nama Truk: ${dooringDetails.truck.name || '-'} (${dooringDetails.truck.registrationNumber || ''})`;
                    if (dooringDetails.driverName) {
                        additionalFields += `\n• Driver: ${dooringDetails.driverName}${dooringDetails.driverPhone ? ' (' + dooringDetails.driverPhone + ')' : ''}`;
                    }
                }
                if (targetTracking.container) {
                    additionalFields += `\n• No. Container: ${targetTracking.container.containerNumber || '-'}`;
                }
                const routes: string[] = [];
                if (dooringDetails.originAddress) routes.push(dooringDetails.originAddress);
                if (dooringDetails.destinationAddress) routes.push(dooringDetails.destinationAddress);
                if (routes.length > 0) {
                    additionalFields += `\n• Rute: ${routes.join(' -> ')}`;
                }
            } else {
                if (targetTracking.container) {
                    additionalFields += `\n• No. Container: ${targetTracking.container.containerNumber || '-'}`;
                } else if (targetTracking.orderTruck) {
                    additionalFields += `\n• Armada/Truk: ${targetTracking.orderTruck.name || '-'}`;
                }
            }

            if (!additionalFields) {
                if ((order.containers || []).length > 0) {
                    additionalFields = '\n' + order.containers.map(c => `• No. Container: ${c.containerNumber || '-'}`).join('\n');
                } else if ((order.trucks || []).length > 0) {
                    additionalFields = '\n' + order.trucks.map(t => `• Armada/Truk: ${t.name || '-'}`).join('\n');
                }
            }

            const goodsList = (order.goods || []).map(g => {
                const name = g.goods?.name || 'Barang';
                const qty = g.quantityAmount || 0;
                return `${name} (Qty: ${qty})`;
            }).join(', ');
            const formattedGoods = goodsList || '-';

            let rawDate: string | undefined = '';
            if (statusKey === 'completed') {
                if (dooringDetails) rawDate = dooringDetails.actualArrivalDate;
                else if (loadDetails) rawDate = loadDetails.actualEndDate;
                else if (unloadingDetails) rawDate = unloadingDetails.actualEndDate;
                else if (shippingDetails) rawDate = shippingDetails.schedule.actualArrival;
                else if (trainDetails) rawDate = trainDetails.schedule.actualArrival;
            } else if (statusKey === 'in_progress') {
                if (dooringDetails) rawDate = dooringDetails.actualDepartureDate;
                else if (loadDetails) rawDate = loadDetails.actualStartDate;
                else if (unloadingDetails) rawDate = unloadingDetails.actualStartDate;
                else if (shippingDetails) rawDate = shippingDetails.schedule.actualDeparture;
                else if (trainDetails) rawDate = trainDetails.schedule.actualDeparture;
            } else {
                if (dooringDetails) rawDate = dooringDetails.estimatedDepartureDate;
                else if (loadDetails) rawDate = loadDetails.estimatedStartDate;
                else if (unloadingDetails) rawDate = unloadingDetails.estimatedStartDate;
                else if (shippingDetails) rawDate = shippingDetails.schedule.estimatedDeparture;
                else if (trainDetails) rawDate = trainDetails.schedule.estimatedDeparture;
            }

            const formattedDate = rawDate ? moment(rawDate).format("DD/MM/YYYY HH:mm") : '-';

            const hour = moment().hour();
            let greeting = 'Selamat pagi';
            if (hour >= 11 && hour < 15) {
                greeting = 'Selamat siang';
            } else if (hour >= 15 && hour < 19) {
                greeting = 'Selamat sore';
            } else if (hour >= 19 || hour < 4) {
                greeting = 'Selamat malam';
            }

            let scheduleInfo = '';
            if (shippingDetails) {
                const sched = shippingDetails.schedule;
                const etd = sched.estimatedDeparture ? moment(sched.estimatedDeparture).format("DD/MM/YYYY HH:mm") : '-';
                const eta = sched.estimatedArrival ? moment(sched.estimatedArrival).format("DD/MM/YYYY HH:mm") : '-';
                const atd = sched.actualDeparture ? moment(sched.actualDeparture).format("DD/MM/YYYY HH:mm") : '-';
                const ata = sched.actualArrival ? moment(sched.actualArrival).format("DD/MM/YYYY HH:mm") : '-';
                
                let delayInfo = '';
                if (sched.delays && sched.delays.length > 0) {
                    const lastDelay = sched.delays[sched.delays.length - 1];
                    const newEta = lastDelay.newEstimatedArrival ? moment(lastDelay.newEstimatedArrival).format("DD/MM/YYYY HH:mm") : '-';
                    delayInfo = `\n• Estimasi Baru (Delay ETA): ${newEta}`;
                }

                scheduleInfo = `\n• Rencana Berangkat (ETD): ${etd}\n• Rencana Tiba (ETA): ${eta}\n• Realisasi Berangkat (ATD): ${atd}\n• Realisasi Tiba (ATA): ${ata}${delayInfo}`;
            } else if (trainDetails) {
                const sched = trainDetails.schedule;
                const etd = sched.estimatedDeparture ? moment(sched.estimatedDeparture).format("DD/MM/YYYY HH:mm") : '-';
                const eta = sched.estimatedArrival ? moment(sched.estimatedArrival).format("DD/MM/YYYY HH:mm") : '-';
                const atd = sched.actualDeparture ? moment(sched.actualDeparture).format("DD/MM/YYYY HH:mm") : '-';
                const ata = sched.actualArrival ? moment(sched.actualArrival).format("DD/MM/YYYY HH:mm") : '-';

                let delayInfo = '';
                if (sched.delays && sched.delays.length > 0) {
                    const lastDelay = sched.delays[sched.delays.length - 1];
                    const newEta = lastDelay.newEstimatedArrival ? moment(lastDelay.newEstimatedArrival).format("DD/MM/YYYY HH:mm") : '-';
                    delayInfo = `\n• Estimasi Baru (Delay ETA): ${newEta}`;
                }

                scheduleInfo = `\n• Rencana Berangkat (ETD): ${etd}\n• Rencana Tiba (ETA): ${eta}\n• Realisasi Berangkat (ATD): ${atd}\n• Realisasi Tiba (ATA): ${ata}${delayInfo}`;
            } else if (dooringDetails) {
                const etd = dooringDetails.estimatedDepartureDate ? moment(dooringDetails.estimatedDepartureDate).format("DD/MM/YYYY HH:mm") : '-';
                const eta = dooringDetails.estimatedArrivalDate ? moment(dooringDetails.estimatedArrivalDate).format("DD/MM/YYYY HH:mm") : '-';
                const atd = dooringDetails.actualDepartureDate ? moment(dooringDetails.actualDepartureDate).format("DD/MM/YYYY HH:mm") : '-';
                const ata = dooringDetails.actualArrivalDate ? moment(dooringDetails.actualArrivalDate).format("DD/MM/YYYY HH:mm") : '-';
                scheduleInfo = `\n• Rencana Berangkat (ETD): ${etd}\n• Rencana Tiba (ETA): ${eta}\n• Realisasi Berangkat (ATD): ${atd}\n• Realisasi Tiba (ATA): ${ata}`;
            } else if (loadDetails) {
                const estStart = loadDetails.estimatedStartDate ? moment(loadDetails.estimatedStartDate).format("DD/MM/YYYY HH:mm") : '-';
                const estEnd = loadDetails.estimatedEndDate ? moment(loadDetails.estimatedEndDate).format("DD/MM/YYYY HH:mm") : '-';
                const actStart = loadDetails.actualStartDate ? moment(loadDetails.actualStartDate).format("DD/MM/YYYY HH:mm") : '-';
                const actEnd = loadDetails.actualEndDate ? moment(loadDetails.actualEndDate).format("DD/MM/YYYY HH:mm") : '-';
                scheduleInfo = `\n• Estimasi Mulai: ${estStart}\n• Estimasi Selesai: ${estEnd}\n• Realisasi Mulai: ${actStart}\n• Realisasi Selesai: ${actEnd}`;
            } else if (unloadingDetails) {
                const estStart = unloadingDetails.estimatedStartDate ? moment(unloadingDetails.estimatedStartDate).format("DD/MM/YYYY HH:mm") : '-';
                const estEnd = unloadingDetails.estimatedEndDate ? moment(unloadingDetails.estimatedEndDate).format("DD/MM/YYYY HH:mm") : '-';
                const actStart = unloadingDetails.actualStartDate ? moment(unloadingDetails.actualStartDate).format("DD/MM/YYYY HH:mm") : '-';
                const actEnd = unloadingDetails.actualEndDate ? moment(unloadingDetails.actualEndDate).format("DD/MM/YYYY HH:mm") : '-';
                scheduleInfo = `\n• Estimasi Mulai: ${estStart}\n• Estimasi Selesai: ${estEnd}\n• Realisasi Mulai: ${actStart}\n• Realisasi Selesai: ${actEnd}`;
            }

            const variations = [
                `${greeting} Bapak/Ibu ${clientName},\n\nBerikut kami sampaikan update status pengiriman terbaru untuk No. Order ${orderNo}:\n\n• Status Terakhir: ${trackingTypeName} - ${statusLabel}\n• Tanggal/Waktu: ${formattedDate}${additionalFields}\n• Detail Barang: ${formattedGoods}${scheduleInfo}`,
                `Yth. Bapak/Ibu ${clientName},\n\nPembaruan informasi pengiriman terbaru untuk No. Order ${orderNo}:\n\n- Status Terakhir: ${trackingTypeName} - ${statusLabel}\n- Tanggal/Waktu: ${formattedDate}${additionalFields.replace(/•/g, '-')}\n- Deskripsi Barang: ${formattedGoods}${scheduleInfo.replace(/•/g, '-')}`,
                `Halo Bapak/Ibu ${clientName},\n\nBerikut adalah laporan posisi/status pengiriman terkini dengan No. Order ${orderNo}:\n\n» Status Terkini: ${trackingTypeName} - ${statusLabel}\n» Waktu Update: ${formattedDate}${additionalFields.replace(/•/g, '»')}\n» Rincian Barang: ${formattedGoods}${scheduleInfo.replace(/•/g, '»')}`
            ];

            const randIdx = Math.floor(Math.random() * variations.length);
            const text = variations[randIdx];

            const appScheme = GlobalConfig.mobileAppScheme || 'jejak';
            const mobileDeeplink = `${appScheme}://tracking?orderNumber=${orderNo}`;
            const textWithLink = `${text}\n\natau dapat dilihat di: ${mobileDeeplink}`;

            setTrackingMessageText(textWithLink);
        }
    }, [activeTracking, activeSurface, order, randomTrigger, trackings]);

    useEffect(() => {
        _onRetrieveTrackings();
    }, []);

    const _onRetrieveTrackings = async (pageNumber?: number,) => {
        try {
            setLoaded(false);

            const _order = await OrderService.get(props.orderId);
            setOrder(_order);

            const _trackings = await OrderTrackingService.retrieve(props.orderId);
            const _sorted: {date: string, tracking: IOrderTrackingResourceShortProps}[] = [];
            _trackings.forEach((tracking) => {
                let date: string | undefined;
                if (tracking.dooringDetails) {
                    date = tracking.dooringDetails.actualDepartureDate || tracking.dooringDetails.estimatedDepartureDate;
                } else if (tracking.loadDetails) {
                    date = tracking.loadDetails.actualStartDate || tracking.loadDetails.estimatedStartDate;
                } else if (tracking.unloadingDetails) {
                    date = tracking.unloadingDetails.actualStartDate || tracking.unloadingDetails.estimatedStartDate;
                } else if (tracking.shippingDetails) {
                    date = tracking.shippingDetails.schedule.actualDeparture || tracking.shippingDetails.schedule.estimatedDeparture;
                } else if (tracking.trainDetails) {
                    date = tracking.trainDetails.schedule.actualDeparture || tracking.trainDetails.schedule.estimatedDeparture;
                }

                if (date) {
                    const idx = _sorted.findIndex((s) => s.date && date && s.date > date);
                    if (idx < 0) {
                        _sorted.push({date, tracking});
                    } else {
                        _sorted.splice(idx, 0, {date, tracking});
                    }
                }
            });

            setTrackings(_sorted.map((s) => s.tracking));

            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_order_trackings_error',
                text: 'Failed to retrieve order trackings. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.containerId && !props.truckId) {
            items.push({
                key: "filter_by_container",
                text: "Filter by container",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    const options: IDropdownOption[] = [];

                    let allText: string = 'All container(s)';
                    if (order?.goodsType === 'fcl' || order?.goodsType === 'lcl') {
                        allText = "All container(s)";
                    } else if (order?.goodsType === 'ftl' || order?.goodsType === 'ltl') {
                        allText = "All truck(s)";
                    }

                    options.push({ key: "all", text: allText });
                    (order?.containers || []).forEach((container, idx) => {
                        options.push({ key: container.id, text: "Container " + (container.containerNumber || `#${idx + 1}`) })
                    });
                    (order?.trucks || []).forEach((truck, idx) => {
                        options.push({ key: truck.id, text: truck.name || `Truck ${idx + 1}` })
                    });

                    return <Dropdown styles={{ root: { width: 300 } }}
                        options={options}
                        selectedKey={selectedFilter}
                        onChange={(evt, opt) => {
                            const value = opt ? opt.key as string : 'all';
                            setSelectedFilter(value);
                        }} />;
                }
            });
        }

        if (!props.hideCreateTracking && PermissionsService.hasPermission(['orders.update.all'], user.permissions)) {
            farItems.push({
                key: "register",
                text: "Create Tracking",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <PrimaryButton text={"Create Tracking"}
                        iconProps={{ iconName: "Add" }}
                        onClick={() => { setActiveSurface('create') }}
                        styles={{ root: { marginLeft: 10 } }} />;
                }
            });

            farItems.push({
                key: "changeOrder",
                text: "Change Order",
                onRender: () => {
                    return <DefaultButton text={"Change Order"}
                        onClick={() => { setActiveSurface('sort') }}
                        styles={{ root: { marginLeft: 10 } }} />;
                }
            })
        }

        if (order && trackings.length > 0) {
            farItems.push({
                key: 'copyPublicTrackingLink',
                text: 'Copy Public Link',
                onRender: () => {
                    const onClick = () => {
                        const deeplink = `${GlobalConfig.publicHostname}/tracking?searchBy=on&identifier=${order.orderNumber}`;
                        navigator.clipboard.writeText(deeplink);
                        banner.add({
                            key: 'copy_link',
                            text: 'Link copied to clipboard',
                            icon: faCheck,
                            variant: 'success'
                        });
                    };
                    return <DefaultButton text={'Copy Public Link'} onRenderIcon={() => <FontAwesomeIcon icon={faCopy} />} onClick={onClick} styles={{ root: { marginLeft: 10 } }} />;
                }
            });

            farItems.push({
                key: 'trackingMessage',
                text: 'Tracking Message',
                onRender: () => {
                    return <DefaultButton text={'Tracking Message'} onRenderIcon={() => <FontAwesomeIcon icon={faComment} />} onClick={() => setActiveSurface('share.message')} styles={{ root: { marginLeft: 10 } }} />;
                }
            });
        }

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveTracking(undefined);
        setActiveContainer(undefined);
        setActiveOrderTruck(undefined);

        if (refresh) { _onRetrieveTrackings() }
    }

    const onUpload = async (files: IFileDetailsProps[]) => {
        if (activeTracking)
            try {
                const names: string[] = [];
                const fd = new FormData();

                files.map(f => {
                    const file = f.data as File
                    const allMetaData = {
                        name: file.name,
                        size: file.size,
                    };

                    fd.append('metadatas[]', JSON.stringify({ 
                        visibility: f.visibility, 
                        type: activeTracking.type, 
                        orderTrackingId: activeTracking.id,
                        ...allMetaData
                    }));
                });

                files.map(f => fd.append('documents[]', f.data));

                await OrderService.documents.new(activeTracking.orderId, fd);

                banner.add({
                    key: "upload_order_documents_success",
                    variant: 'success',
                    icon: faCheck,
                    text: `Document(s) ${names.join(", ")} uploaded successfully.`
                });

                setActiveSurface(undefined);
            } catch (e) {
                throw (e);
            }
    }

    const getFilteredAndGroupedTrackings = () => {
        if (!order) return [];

        const rawFiltered = trackings.filter((tracking) => {
            if (order.goodsType === 'fcl' || order.goodsType === 'lcl') {
                return selectedFilter !== 'all' && tracking.container ? tracking.container.id === selectedFilter : true;
            } else if (order.goodsType === 'ftl' || order.goodsType === 'ltl') {
                return selectedFilter !== 'all' && tracking.orderTruck ? tracking.orderTruck.id === selectedFilter : true;
            } else {
                return true;
            }
        });

        if (selectedFilter !== 'all') {
            return rawFiltered.map(t => ({
                key: t.id,
                primaryTracking: t,
                containers: t.container ? [t.container] : (order.containers || []).filter(c => c.id === selectedFilter),
                trucks: t.orderTruck ? [t.orderTruck] : (order.trucks || []).filter(tr => tr.id === selectedFilter)
            }));
        }

        const groupsMap: { [key: string]: { key: string; primaryTracking: IOrderTrackingResourceShortProps; containers: IOrderContainerResourceShortProps[]; trucks: IOrderTruckResourceShortProps[] } } = {};
        const result: { key: string; primaryTracking: IOrderTrackingResourceShortProps; containers: IOrderContainerResourceShortProps[]; trucks: IOrderTruckResourceShortProps[] }[] = [];

        rawFiltered.forEach((t) => {
            let groupKey = t.id;
            if (t.shippingDetails?.schedule?.id) {
                groupKey = `ship_${t.shippingDetails.schedule.id}`;
            } else if (t.trainDetails?.schedule?.id) {
                groupKey = `train_${t.trainDetails.schedule.id}`;
            }

            if (!groupsMap[groupKey]) {
                groupsMap[groupKey] = {
                    key: groupKey,
                    primaryTracking: t,
                    containers: [],
                    trucks: []
                };
                result.push(groupsMap[groupKey]);
            }

            if (t.container) {
                if (!groupsMap[groupKey].containers.some(c => c.id === t.container!.id)) {
                    groupsMap[groupKey].containers.push(t.container);
                }
            }
            if (t.orderTruck) {
                if (!groupsMap[groupKey].trucks.some(tr => tr.id === t.orderTruck!.id)) {
                    groupsMap[groupKey].trucks.push(t.orderTruck);
                }
            }
        });

        result.forEach(g => {
            if (g.containers.length === 0 && order.containers && order.containers.length > 0) {
                g.containers = order.containers;
            }
            if (g.trucks.length === 0 && order.trucks && order.trucks.length > 0) {
                g.trucks = order.trucks;
            }
        });

        return result;
    };

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {!props.hideCommandBar ? <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" /> : null}
        <Stack className={styles[props.variant || 'card']}>
            {!loaded ? <LoadingComponent label='Retrieving order trackings ...' labelPosition='right' spinnerPosition='baseline' /> : null}
            {loaded && order && trackings.length > 0 ? <Stack className={styles.timeline}>
                <Stack tokens={{ childrenGap: 10 }}>
                    {getFilteredAndGroupedTrackings().map((group) => {
                        return <OrderTrackingDetailsCard
                            key={group.key}
                            showAsTimeline={true}
                            tracking={group.primaryTracking}
                            containers={group.containers}
                            trucks={group.trucks}
                            onOpenSurface={(key, data) => {
                                setActiveSurface(key);
                                setActiveTracking(group.primaryTracking);
                                if (data) {
                                    if (key === 'container.details') {
                                        setActiveContainer(data);
                                    } else if (key === 'ordertruck.details') {
                                        setActiveOrderTruck(data);
                                    }
                                }
                            }}
                        />;
                    })}
                </Stack>
            </Stack> : null}
            {loaded && trackings.length < 1 ? <Stack styles={{ root: { padding: 10 } }}>
                <Text>Order tracking(s) not found</Text>
            </Stack> : null}
        </Stack>
        {activeSurface === 'create' ? <OrderTrackingForm orderId={props.orderId} containerId={props.containerId} truckId={props.truckId} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'sort' && order ? <SortOrderTrackingForm order={order} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'update.details' && activeTracking ? <OrderTrackingForm orderId={props.orderId} trackingId={activeTracking.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'update.status' && activeTracking ? <OrderTrackingStatusForm orderId={props.orderId} trackingId={activeTracking.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'delete' && activeTracking ? <DeleteOrderTrackingForm orderId={props.orderId} tracking={activeTracking} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'container.details' && activeContainer ? <OrderContainerDetails orderId={props.orderId} containerId={activeContainer.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'ordertruck.details' && activeOrderTruck ? <OrderTruckDetails orderId={props.orderId} truckId={activeOrderTruck.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'update.train.status' && activeTracking && activeTracking.trainDetails ? <TrainScheduleStatusForm scheduleId={activeTracking.trainDetails.schedule.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'update.ship.status' && activeTracking && activeTracking.shippingDetails ? <ShipScheduleStatusForm scheduleId={activeTracking.shippingDetails.schedule.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'upload.files' && activeTracking ? <UploadFilesPanel multiple allowSelectDocumentType={false} allowedTypes={['word', 'pdf', 'image']} onUpload={onUpload} onCancel={_onSurfaceDismissed} /> : null}
        {activeSurface === 'share.message' ? (
            <Panel
                headerText="Generate Tracking Message"
                isOpen={true}
                type={PanelType.medium}
                onDismiss={() => setActiveSurface(undefined)}
                isFooterAtBottom={true}
                onRenderFooterContent={() => {
                    const copyToClipboard = () => {
                        navigator.clipboard.writeText(trackingMessageText);
                        banner.add({
                            key: 'copy_tracking_message_success',
                            text: 'Tracking message copied to clipboard!',
                            icon: faCheck,
                            variant: 'success'
                        });
                    };

                    const sendWhatsApp = () => {
                        const rawPhone = order?.customer?.phoneNumber || order?.company?.phoneNumber || '';
                        let cleanPhone = rawPhone.replace(/\D/g, '');
                        if (cleanPhone.startsWith('0')) {
                            cleanPhone = '62' + cleanPhone.substring(1);
                        }
                        const url = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(trackingMessageText)}`;
                        window.open(url, '_blank');
                    };

                    return (
                        <Stack horizontal tokens={{ childrenGap: 10 }}>
                            <PrimaryButton text="Copy Text" onRenderIcon={() => <FontAwesomeIcon icon={faCopy} />} onClick={copyToClipboard} />
                            <DefaultButton text="Send WhatsApp" onRenderIcon={() => <FontAwesomeIcon icon={faPaperPlane} />} onClick={sendWhatsApp} />
                            <DefaultButton text="Close" onClick={() => setActiveSurface(undefined)} />
                        </Stack>
                    );
                }}
            >
                <Stack tokens={{ childrenGap: 20 }} styles={{ root: { marginTop: 20 } }}>
                    <Stack>
                        <Text size="medium" style={{ fontWeight: 'bold' }}>{detectedCategory}</Text>
                    </Stack>
                    <TextField
                        label="Pesan Update Tracking (Variasi Acak)"
                        multiline
                        rows={8}
                        autoAdjustHeight
                        readOnly
                        value={trackingMessageText}
                    />
                    <Stack horizontalAlign="start">
                        <ActionButton
                            onRenderIcon={() => <FontAwesomeIcon icon={faRotate} />}
                            text="Ganti Variasi Pesan (Acak Ulang)"
                            onClick={() => setRandomTrigger(prev => prev + 1)}
                        />
                    </Stack>
                </Stack>
            </Panel>
        ) : null}
    </Stack>;
};

export default OrderTrackingsList;
